import { type FunctionComponent, useCallback, useMemo } from 'react';
import { Card, Header, HStack, Image, Switcher, Text, VStack } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { intersection } from '@direct-frontend/stdlib';
import { trackGoal } from '@/entities/trackGoal';
import {
    type OrderDeliveryMethod,
    OrderDeliveryMethods,
    type OrderPaymentMethod,
    OrderPaymentMethods,
    OrderPostPaymentMethods,
    OrderPrePaymentMethods,
} from '@/shared/api/order';
import { getRequestContext } from '@/shared/lib/requestContext';
import styles from './PaymentMethodSwitcher.module.css';

const CASH_IMAGE_SRC = 'https://yastatic.net/s3/yastore-static/resources/images/cash_gradient.png';

interface PaymentMethodSwitcherProps extends TestProps {
    paymentMethods: OrderPaymentMethod[];
    paymentMethod: OrderPaymentMethod;
    deliveryMethod: OrderDeliveryMethod;
    isDeliveryLoading: boolean;
    isPaymentLoading: boolean;
    isGiftCardApply: boolean;
    price: string;
    onChange: (value: OrderPaymentMethod) => void;
}

export const PaymentMethodSwitcher: FunctionComponent<PaymentMethodSwitcherProps> = ({
    'data-testid': dataTestId = 'PaymentMethodSwitcher',
    paymentMethods,
    paymentMethod,
    deliveryMethod,
    isDeliveryLoading,
    isPaymentLoading,
    isGiftCardApply,
    price,
    onChange,
}) => {
    const {
        delivery: { disablePostPayment, postPaymentMaxPrice, disableOnlinePayment },
    } = getRequestContext();

    const isPostPayment = OrderPostPaymentMethods.includes(paymentMethod);

    const canChangeMethod = useMemo(() => {
        const necessaryMethods = isPostPayment ? OrderPrePaymentMethods : OrderPostPaymentMethods;

        return intersection(necessaryMethods, paymentMethods).length > 0;
    }, [isPostPayment, paymentMethods]);

    const isPostPaymentDisable = useMemo(
        () => Boolean(postPaymentMaxPrice && price && Number(price) > postPaymentMaxPrice),
        [postPaymentMaxPrice, price]
    );

    const isHideSwitcher = useMemo(() => {
        if (deliveryMethod === OrderDeliveryMethods.SELF_PICK_UP) {
            return false;
        }

        const isCantChangeToPostPayment = !canChangeMethod || disablePostPayment || isPostPaymentDisable;
        const isCantChangeToOnlinePayment = !canChangeMethod || disableOnlinePayment;

        return (!isPostPayment && isCantChangeToPostPayment) || (isPostPayment && isCantChangeToOnlinePayment);
    }, [
        canChangeMethod,
        deliveryMethod,
        disableOnlinePayment,
        disablePostPayment,
        isPostPayment,
        isPostPaymentDisable,
    ]);

    const isSwitcherDisabled = useMemo(
        () => isDeliveryLoading || isPaymentLoading || !canChangeMethod || isGiftCardApply,
        [isDeliveryLoading, isPaymentLoading, canChangeMethod, isGiftCardApply]
    );

    const handleChange = useCallback(
        (value: boolean) => {
            let newMethod: OrderPaymentMethod;

            if (value) {
                newMethod = paymentMethods.includes(OrderPaymentMethods.CARD_ON_DELIVERY)
                    ? OrderPaymentMethods.CARD_ON_DELIVERY
                    : OrderPaymentMethods.CASH_ON_DELIVERY;
            } else {
                newMethod = OrderPaymentMethods.CARD_ONLINE;
            }

            onChange(newMethod);

            trackGoal('payment_type_changed', {
                payment_type: newMethod,
            });
        },
        [onChange, paymentMethods]
    );

    const postPaymentTitle = useMemo(() => {
        const defaultTitle = 'Картой или наличными';

        const methods = OrderPostPaymentMethods.filter((method) => paymentMethods.includes(method));

        if (isGiftCardApply) {
            return 'Недоступна при оплате подарочной картой';
        }

        if (methods.length === OrderPostPaymentMethods.length) {
            return defaultTitle;
        }

        if (methods.includes(OrderPaymentMethods.CARD_ON_DELIVERY)) {
            return 'Картой';
        }

        if (methods.includes(OrderPaymentMethods.CASH_ON_DELIVERY)) {
            return 'Наличными';
        }

        return defaultTitle;
    }, [isGiftCardApply, paymentMethods]);

    if (isHideSwitcher) {
        return null;
    }

    return (
        <Card data-testid={dataTestId} className={styles.card} color="white" borderRadius="20" padding="20">
            <HStack gap="12" alignItems="center">
                <Image src={CASH_IMAGE_SRC} width="28" height="28" alt="Оплата при получении" />

                <VStack className={styles.texts}>
                    <Header level="h4" className={styles.title}>
                        Оплата при получении
                    </Header>

                    {postPaymentTitle && (
                        <Text role="supplementary" size="s" className={styles.subtitle}>
                            {postPaymentTitle}
                        </Text>
                    )}
                </VStack>

                {isGiftCardApply ? null : (
                    <Switcher
                        className={styles.switcher}
                        data-testid={`${dataTestId}.Switcher`}
                        isSelected={isPostPayment}
                        isDisabled={isSwitcherDisabled}
                        onChange={handleChange}
                    />
                )}
            </HStack>
        </Card>
    );
};
